/**
 * Integrytas Group - Base App (MOBILE)
 *
 * Child drill-down sheet for the mobile related-children block.
 *
 * The mobile counterpart of the desktop ChildDetailModal, and deliberately NOT
 * a reuse of it: §16 forbids hosting a desktop component in a mobile viewport,
 * and a centred modal is the wrong idiom on a phone anyway. Tapping a child in
 * a parent's detail sheet opens a SECOND bottom sheet stacked above it, which
 * is the pattern the rest of the mobile app already uses for drill-in.
 *
 * Presentation only differs — the data path is identical to desktop: the same
 * table/pkColumn the group already declares, read through the same gateway.
 *
 * Scope: this renders a read-only view. The desktop drill-down can open a
 * child's real EDIT form because desktop editors are registered by their pages;
 * the mobile equivalent needs mobile edit sheets registered the same way, which
 * is a follow-up. Read-only is the honest state until then — better than
 * hosting the desktop form on a phone, which §16 rules out.
 */
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { X } from 'lucide-react';
import { apiDb } from '../../lib/api_client';
import { hasAccess, type AuthSession } from '../../types/auth';
import { ChildEditorReadOnlyProvider } from '../../lib/child_editors';
import { getMobileChildEditor } from '../lib/mobile_child_editors';
import { MobileDetailRow } from './MobileCard';
import { MobileStatusPill } from './MobileStatusPill';

export interface MobileChildTarget {
  table: string;
  pkColumn: string;
  pk: string | number;
  label: string;
}

/** Surrogate keys and audit plumbing never reach the user (§17.B4). */
const HIDDEN = /^(.*_id|id|created_at|updated_at|modified_by|row_version|ext_attributes)$/i;

function prettify(column: string): string {
  return column.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
}

export function MobileChildDetailSheet({
  target, onClose, session, onSaved,
}: {
  target: MobileChildTarget | null;
  onClose: () => void;
  /** Supply to allow a registered mobile editor to open (and to resolve RBAC). */
  session?: AuthSession;
  onSaved?: () => void;
}) {
  const { t } = useTranslation();
  const entry = getMobileChildEditor(target?.table);
  const [row, setRow] = useState<Record<string, unknown> | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    // A registered editor loads its own record, so skip the generic fetch —
    // but ONLY when that editor will actually render. It needs a session to
    // resolve its verb against, and without one we fall through to the
    // read-only sheet below; skipping the fetch there told the user "Record
    // not found" about a record that exists. Keep this condition identical to
    // the render guard further down.
    if (!target || (entry && session)) { setRow(null); setError(null); return; }
    let cancelled = false;
    void (async () => {
      setBusy(true); setError(null);
      const { data, error: e } = await apiDb
        .from(target.table).select('*').eq(target.pkColumn, target.pk).maybeSingle();
      if (cancelled) return;
      if (e) setError(e.message); else setRow((data as Record<string, unknown>) ?? null);
      setBusy(false);
    })();
    return () => { cancelled = true; };
    // `entry` is a Map lookup keyed by target.table, so its identity is stable
    // for a given target — listing it is correct and does not add a refetch.
  }, [target, entry, session]);

  // The verb belongs to the CHILD's page, not the parent's — same rule as
  // desktop. Without it the editor still opens, read-only.
  if (target && entry && session) {
    const readOnly = !hasAccess(session, entry.pageCode, 'change');
    const { Editor } = entry;
    return (
      <ChildEditorReadOnlyProvider value={readOnly}>
        <Editor
          session={session}
          pk={target.pk}
          readOnly={readOnly}
          onClose={onClose}
          onSaved={() => { onSaved?.(); onClose(); }}
        />
      </ChildEditorReadOnlyProvider>
    );
  }

  const open = !!target;
  const fields = row
    ? Object.entries(row).filter(([k, v]) => !HIDDEN.test(k) && v !== null && v !== '')
    : [];

  return (
    <>
      {/* Stacked above the parent sheet, so its own scrim sits over that one. */}
      <div className={'m-scrim' + (open ? ' m-open' : '')} style={{ zIndex: 70 }} onClick={onClose} />
      <div
        className={'m-sheet' + (open ? ' m-open' : '')}
        style={{ zIndex: 71 }}
        aria-label={t('mobile.related.child_detail', 'Related record')}
      >
        <div className="m-grabber" />
        {target && (
          <>
            <div className="m-sheet-id">
              <span>
                <div className="m-nm">{target.label}</div>
              </span>
              <button
                type="button"
                className="m-btn m-btn-ghost"
                onClick={onClose}
                aria-label={t('mobile.close', 'Close')}
                style={{ marginLeft: 'auto' }}
              >
                <X size={16} />
              </button>
            </div>
            <div className="m-sheet-body">
              {busy && <div className="m-rc-loading">{t('mobile.related.loading', 'Loading…')}</div>}
              {error && <div className="m-err">{error}</div>}
              {!busy && !error && !row && (
                <div className="m-rc-empty">{t('mobile.related.not_found', 'Record not found')}</div>
              )}
              {!busy && !error && row && fields.map(([k, v]) => (
                <MobileDetailRow
                  key={k}
                  label={prettify(k)}
                  /* §17.B10: a Y/N char(1) never renders as a bare letter. */
                  value={v === 'Y' || v === 'N'
                    ? <MobileStatusPill tone={v === 'Y' ? 'ok' : 'no'}>{v === 'Y'
                        ? t('common.boolean.yes', 'Yes')
                        : t('common.boolean.no', 'No')}</MobileStatusPill>
                    : String(v)}
                />
              ))}
            </div>
          </>
        )}
      </div>
    </>
  );
}
